import React from 'react'
import styled from '@emotion/styled'
import HorizontalProgressBar from '../../general/HorizontalProgressBar'
import {css} from '@emotion/core'
import {theme} from '../../../../themes/findhotel'

interface Percentage {
  /** Percentage (0-100) position relative to the StepBar */
  percentage: number
}

const circlePosition = ({percentage}: Percentage) => css`
  left: ${percentage}%;
  position: absolute;
  transform: translateX(-50%);
  z-index: 1;
  display: flex;
  align-items: center;
`

interface StepBarMarkerProp extends Percentage {
  /** Theme from atlas */
  theme: typeof theme
  /** Should the marker be active/highlighted */
  isActive?: boolean
}
export const StepBarCustomMarker = styled.div<StepBarMarkerProp>(circlePosition)
export const StepBarMarker = styled.div<StepBarMarkerProp>(
  ({theme, isActive, percentage}) => css`
    border-radius: 50%;
    width: 10px;
    height: 10px;
    background: ${isActive ? theme.colors.privateDealColor : 'transparent'};
    left: ${percentage}%;
    box-shadow: 0 0 0 4px ${theme.colors.violet.v200};

    ${circlePosition({percentage})}
    z-index: 0;
  `
)

const StepBarProgress = styled('div')`
  position: relative;
  width: 100%;
  margin-bottom: ${theme.layout.spacing.s500}px;
`

const RELATIVE_CENTER_POINT = {
  center: '-50%',
  left: '0%',
  right: '-100%'
}
interface StepBarLabelProps extends Percentage {
  /** Theme from atlas */
  theme: typeof theme
  /**
   * This prop controls how the component should be positioned relative to the StepBar.
   * It is useful when one wants to position the label with it's marker without having to
   * recur to weird position values.
   * In summary, it affects it's transform translate property.
   *
   * @example
   * This label should be aligned to the left of the marker, used at the start of the StepBar
   * ```
   * <StepBarLabel centerPoint="left" position="0">
   * ```
   * @example
   * This label should be aligned to the right of the marker, used at the end of the StepBar
   * ```
   * <StepBarLabel centerPoint="right" position="100">
   * ```
   * @example
   * This label should be centered with the marker
   * ```
   * <StepBarLabel centerPoint="center" position="30">
   * ```
   */
  centerPoint?: 'center' | 'left' | 'right'
}
export const StepBarLabel = styled.div<StepBarLabelProps>(
  ({theme, percentage, centerPoint = 'center'}) => css`
    position: absolute;
    left: ${percentage}%;
    top: ${theme.layout.spacing.s600}px;
    transform: translateX(${RELATIVE_CENTER_POINT[centerPoint]});
    white-space: nowrap;
  `
)

interface PropsType extends Percentage {
  /**
   * Steps to be rendered.
   * Should be used with StepBarMarker, StepBarCustomMarker and StepBarLabel.
   */
  children: React.ReactNode
}

const StepBar = ({percentage, children: steps}: PropsType) => (
  <StepBarProgress>
    <HorizontalProgressBar value={percentage} appearance="stepBar">
      {steps}
    </HorizontalProgressBar>
  </StepBarProgress>
)

export default StepBar
